import { Card, CardContent, CardHeader, CardTitle } from "@paribus/shared-ui";
import { Check } from "lucide-react";

import { PlanButton } from "./PlanButton";
import type { PricingPlan } from "./Plans";

interface PlanCardProps {
  plan: PricingPlan;
  isPlanCurrent: boolean;
  hasActiveSubscription: boolean;
  activePlanIsTiered?: boolean;
  activeTierOrder?: number | null;
  isLoading: boolean;
  isLoadingManageSubscription: boolean;
  showFeatures: boolean;
  onPlanSelect?: (plan: PricingPlan) => void | Promise<void>;
  onManageSubscription?: () => void | Promise<void>;
  formatPrice: (amount: number, currency: string, billingCycle: string) => string;
  formatPages: (pages: number) => string;
  formatOverusagePrice: (amount: number, currency: string) => string;
  disabled?: boolean;
}

export const PlanCard = ({
  plan,
  isPlanCurrent,
  hasActiveSubscription,
  activePlanIsTiered = false,
  activeTierOrder = null,
  isLoading,
  isLoadingManageSubscription,
  showFeatures,
  onPlanSelect,
  onManageSubscription,
  formatPrice,
  formatPages,
  formatOverusagePrice,
  disabled = false,
}: PlanCardProps) => {
  const hasBadge = (plan.isPopular && !isPlanCurrent) || isPlanCurrent;

  return (
    <div className={`relative w-full max-w-[320px] sm:max-w-72 ${hasBadge ? "mt-8 md:mt-0" : ""}`}>
      {plan.isPopular && !isPlanCurrent && (
        <div className="absolute -top-4 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-4 py-1 text-sm font-bold text-white shadow-md">
          Most Popular
        </div>
      )}
      {isPlanCurrent && (
        <div className="absolute -top-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-emphasis px-4 py-1 text-sm font-bold text-white shadow-md">
          <Check className="h-4 w-4" aria-hidden="true" />
          Plan Activated
        </div>
      )}
      <Card
        className={`flex h-full w-full flex-col gap-2 rounded-2xl border-2 p-6 pb-8 ${
          isPlanCurrent
            ? "border-primary-emphasis bg-card shadow-xl"
            : plan.isPopular
              ? "border-primary bg-primary-subtle shadow-xl"
              : "border-border"
        } transition-all hover:shadow-lg`}
      >
        <CardHeader className="flex flex-col gap-2">
          <div>
            <CardTitle className="flex items-center justify-center text-xl font-bold text-foreground">
              {plan.name}
            </CardTitle>
            <p className="mt-2 min-h-[2.5rem] text-center text-sm text-muted-foreground">
              {plan.description}
            </p>
          </div>
          <div className="mt-6 flex flex-col items-center justify-center gap-2">
            <p className="text-3xl font-bold text-foreground">
              {formatPrice(plan.price, plan.currency, plan.billingCycle)}
            </p>
            <div className="text-center">
              <p className="mt-1 text-sm text-muted-foreground">{formatPages(plan.pages)}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {plan.overusagePricing
                  ? formatOverusagePrice(plan.overusagePricing, plan.currency)
                  : ""}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-grow">
          <PlanButton
            plan={plan}
            isPlanCurrent={isPlanCurrent}
            hasActiveSubscription={hasActiveSubscription}
            activePlanIsTiered={activePlanIsTiered}
            activeTierOrder={activeTierOrder}
            isLoading={isLoading}
            isLoadingManageSubscription={isLoadingManageSubscription}
            onPlanSelect={onPlanSelect}
            onManageSubscription={onManageSubscription}
            disabled={disabled}
          />
          {showFeatures && plan.features && plan.features.length > 0 && (
            <ul className="mt-6 space-y-3">
              {plan.features.map((feature: string) => (
                <li key={feature} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
